import { useState } from "react";
import { API_KEY } from "../constants.js";


/**
 * Custom hook for handling geocoding operations
 * @param {Function} onError - Callback for handling errors
 * @returns {Object} Geocoding methods and state
 */
const useGeocode = (onError) => {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Perform reverse geocoding to get address data from coordinates
   * @param {number} lat - Latitude
   * @param {number} lng - Longitude
   * @returns {Object|null} Location data or null if failed
   */
  const reverseGeocode = async (lat, lng) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${API_KEY}`
      );
      const json = await response.json();

      if (json.status === "OK") {
        const locationData = {};
        json.results[0].address_components.forEach((component) => {
          const type = component.types[0];
          if (type === "country") {
            locationData.country = component.long_name;
            locationData.country_short = component.short_name || "";
          } else if (type === "administrative_area_level_1") {
            locationData.province = component.long_name;
            locationData.province_short = component.short_name || "";
          } else if (type === "locality") {
            locationData.city = component.long_name;
          }
        });
        return locationData;
      } else {
        onError && onError("Failed to retrieve location data");
        return null;
      }
    } catch (error) {
      onError && onError(`Error: ${error.message}`);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Geocode an address string to coordinates
   * @param {string} address - Address to geocode
   * @returns {Object|null} Coordinates or null if failed
   */
  const geocodeAddress = async (address) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=${API_KEY}`
      );
      const json = await response.json();

      if (json.status === "OK") {
        const { lat, lng } = json.results[0].geometry.location;
        return { lat, lng };
      } else {
        onError && onError("Couldn't find that location");
        return null;
      }
    } catch (error) {
      onError && onError(`Error: ${error.message}`);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    reverseGeocode,
    geocodeAddress,
    isLoading,
  };
};

export default useGeocode;
